import { useEffect, useState } from 'react';
import type { Tenant, TenantProfileInfo } from '@shared/types';
import { useAuthStore } from '../app/authStore';
import { api, ApiError, describeError } from '../lib/api';
import { Badge, Button, Card, CardHeader, PageHeader, Skeleton } from '../components/ui';
import { Field, Input, Notice } from '../components/form';

interface VerificationResponse {
  tenant: Tenant;
  profile: TenantProfileInfo | null;
}

const STATUS_LABEL = {
  pending: '审核中',
  verified: '已通过',
  rejected: '未通过',
} as const;

const STATUS_TONE = {
  pending: 'info',
  verified: 'ok',
  rejected: 'danger',
} as const;

/** 企业资质：查看审核状态、提交或重新提交资料。 */
export function OrgVerificationPage() {
  const session = useAuthStore((state) => state.session);
  const refresh = useAuthStore((state) => state.refresh);

  const [data, setData] = useState<VerificationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [legalName, setLegalName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [industry, setIndustry] = useState('');
  const [website, setWebsite] = useState('');
  const [licenseNo, setLicenseNo] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  function fill(profile: TenantProfileInfo | null, tenantName: string) {
    setLegalName(profile?.legalName ?? tenantName);
    setContactName(profile?.contactName ?? session?.user.name ?? '');
    setContactPhone(profile?.contactPhone ?? session?.user.phone ?? '');
    setIndustry(profile?.industry ?? '');
    setWebsite(profile?.website ?? '');
    setLicenseNo(profile?.licenseNo ?? '');
  }

  useEffect(() => {
    let cancelled = false;
    api
      .get<VerificationResponse>('/api/org/verification')
      .then((result) => {
        if (cancelled) return;
        setData(result);
        fill(result.profile, result.tenant.name);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setLoadError(caught instanceof ApiError ? caught.message : describeError(caught));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit() {
    setSaving(true);
    setError(null);
    setFieldErrors({});
    setSaved(false);
    try {
      const result = await api.post<VerificationResponse>('/api/org/verification', {
        legalName: legalName.trim(),
        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),
        industry: industry.trim() || undefined,
        website: website.trim() || undefined,
        licenseNo: licenseNo.trim() || undefined,
      });
      setData(result);
      setSaved(true);
      await refresh();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFieldErrors(caught.fieldErrors);
        setError(caught.message);
      } else {
        setError(describeError(caught));
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-5">
        <PageHeader title="企业资质" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div className="space-y-5">
        <PageHeader title="企业资质" />
        <Notice tone="danger">{loadError ?? '读取失败'}</Notice>
      </div>
    );
  }

  if (data.tenant.kind !== 'enterprise') {
    return (
      <div className="space-y-5">
        <PageHeader title="企业资质" description="个人账号无需资质审核。" />
        <Notice tone="info">
          当前是个人组织。若需要以企业身份采购、邀请成员与开票，请在同一账号下新建企业组织。
        </Notice>
      </div>
    );
  }

  const status = data.tenant.verificationStatus;

  return (
    <div className="space-y-5">
      <PageHeader
        title="企业资质"
        description="审核通过后，组织才能邀请成员、生成计费 API Key 与申请发票。"
      />

      <Card>
        <CardHeader
          title={data.tenant.name}
          hint="资料变更后需要重新审核"
          action={<Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>}
        />

        {status === 'rejected' && data.profile?.rejectReason ? (
          <div className="mb-4">
            <Notice tone="danger">驳回原因：{data.profile.rejectReason}</Notice>
          </div>
        ) : null}

        {status === 'pending' ? (
          <div className="mb-4">
            <Notice tone="info">资料已提交，我们会在 1–2 个工作日内完成审核。</Notice>
          </div>
        ) : null}

        {saved ? (
          <div className="mb-4">
            <Notice tone="ok">资料已提交，状态已更新为审核中。</Notice>
          </div>
        ) : null}

        {error ? (
          <div className="mb-4">
            <Notice tone="danger">{error}</Notice>
          </div>
        ) : null}

        <div className="space-y-4">
          <Field label="企业名称" htmlFor="legalName" error={fieldErrors.legalName} required>
            <Input id="legalName" value={legalName} onChange={setLegalName} maxLength={60} />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="联系人" htmlFor="contactName" error={fieldErrors.contactName} required>
              <Input id="contactName" value={contactName} onChange={setContactName} />
            </Field>
            <Field label="联系电话" htmlFor="contactPhone" error={fieldErrors.contactPhone} required>
              <Input id="contactPhone" type="tel" value={contactPhone} onChange={setContactPhone} />
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="行业" htmlFor="industry" error={fieldErrors.industry}>
              <Input id="industry" value={industry} onChange={setIndustry} placeholder="选填" />
            </Field>
            <Field label="网址" htmlFor="website" error={fieldErrors.website}>
              <Input id="website" value={website} onChange={setWebsite} placeholder="选填" />
            </Field>
          </div>
          <Field label="营业执照号" htmlFor="licenseNo" error={fieldErrors.licenseNo} hint="选填；审核时可能需要补充">
            <Input id="licenseNo" value={licenseNo} onChange={setLicenseNo} />
          </Field>

          <Button variant="primary" loading={saving} onClick={submit}>
            保存并提交审核
          </Button>
        </div>
      </Card>
    </div>
  );
}
